import React from "react";
import { useNavigate } from "react-router-dom";
import { ReactComponent as StatusLogo } from "../../../../vendor/logo_.svg";
import {
  Box,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
  Skeleton,
} from "@mui/material";

import {
  Edit as EditIcon,
} from "@mui/icons-material";
import { User as UserIcon } from "react-feather";
import RemoveIcon from "@mui/icons-material/RemoveCircleOutline";
import GavelOutlinedIcon from "@mui/icons-material/GavelOutlined";

import useRule from "../../../../hooks/super/useRule";

import { CrsRuleNo, CrsSecRuleId } from "../../../../utils/constants";
import { formatDate } from "../../../../utils/format";

import DeleteCustomRuleModal from "./M_DeleteRule";
import { IOSSwitch } from "../../../../components/pages/common/styled";
import { getComparator, stableSort } from "../../../../utils/tableSort";
import { getRuleActionString } from "../../../../components/pages/application/waf/rule/custom/component";
import {
  TOKENS,
  RowMenu,
  EmptyState,
} from "../../../../components/pages/_new/super/design";

const headCells = [
  { id: "custom_rule_id", label: "Rule ID", sortable: true },
  { id: "description", label: "Description", sortable: true },
  { id: "action", label: "Action", sortable: true },
  { id: "created_date", label: "Created", sortable: true },
  { id: "ownership", label: "Type", sortable: false },
  { id: "enabled", label: "Enabled", sortable: true },
  { id: "_action", label: "", sortable: false },
];

const HeaderCell = ({ children, sortable, id, orderBy, order, onSort, align }) => (
  <TableCell
    align={align || "left"}
    sx={{
      padding: "10px 14px",
      borderBottom: `1px solid ${TOKENS.border}`,
      background: TOKENS.bgSunken,
      color: TOKENS.fg2,
      fontSize: 11.5,
      fontWeight: 600,
      textTransform: "uppercase",
      letterSpacing: "0.06em",
    }}
  >
    {sortable ? (
      <TableSortLabel
        active={orderBy === id}
        direction={orderBy === id ? order : "asc"}
        onClick={() => onSort(id)}
        sx={{
          color: `${TOKENS.fg2} !important`,
          "& .MuiTableSortLabel-icon": { color: `${TOKENS.fg2} !important` },
        }}
      >
        {children}
      </TableSortLabel>
    ) : (
      children
    )}
  </TableCell>
);

const DataCell = ({ children, align }) => (
  <TableCell
    align={align || "left"}
    sx={{
      padding: "12px 14px",
      borderBottom: `1px solid ${TOKENS.borderSubtle}`,
      color: TOKENS.fg1,
      fontSize: 13.5,
    }}
  >
    {children}
  </TableCell>
);

function CustomRuleTable({ disabled }) {
  const navigate = useNavigate();
  const { customRules, configCrsSecRule } = useRule();
  const [rows, setRows] = React.useState(null);

  const [order, setOrder] = React.useState("desc");
  const [orderBy, setOrderBy] = React.useState("created_date");
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(5);

  const [ruleID, setRuleID] = React.useState(null);
  const [openDelete, setOpenDelete] = React.useState(false);
  const [deleted, setDeleted] = React.useState(false);
  const [removeFlag, setRemoveFlag] = React.useState(false);

  React.useEffect(() => {
    setRows(customRules);
  }, [customRules]);

  const handleSort = (id) => {
    if (id === orderBy) setOrder(order === "asc" ? "desc" : "asc");
    else {
      setOrderBy(id);
      setOrder("asc");
    }
  };

  const editClick = (id) => {
    navigate(`/super/application/rule/custom/edit/${id}`);
  };
  const removeClick = (id, _deleted) => {
    setRuleID(id);
    setDeleted(!!_deleted);
    setRemoveFlag(true);
    setOpenDelete(true);
  };

  const customRuleChange = (custom_rule_id, enable) => {
    let index = -1;
    for (let i = 0; i < rows?.length; i++) {
      if (rows[i]?.custom_rule_id === custom_rule_id) {
        index = i;
        break;
      }
    }
    if (-1 < index) {
      const value = {
        enable,
        rule_id: CrsRuleNo.CUSTOM_GLOBAL,
        sec_rule_id: custom_rule_id.toString(),
      };
      const list = [...rows];
      list.splice(index, 1, { ...rows[index], enabled: enable });
      setRows(list);
      configCrsSecRule(value);
    }
  };

  const sorted = React.useMemo(
    () => stableSort(Array.isArray(rows) ? rows : [], getComparator(order, orderBy)),
    [rows, order, orderBy]
  );

  const isLoading = rows === null;
  const isEmpty = !isLoading && sorted.length === 0;
  const count = Array.isArray(rows) ? rows.length : 0;
  const last = Math.max(0, Math.ceil(count / rowsPerPage) - 1);
  const paged = sorted.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box>
      <Box
        sx={{
          border: `1px solid ${TOKENS.border}`,
          borderRadius: `${TOKENS.radiusM}px`,
          background: TOKENS.bgSurface,
          boxShadow: TOKENS.shadowXs,
          overflow: "hidden",
        }}
      >
        <TableContainer>
          <Table sx={{ minWidth: 900 }}>
            <TableHead>
              <TableRow>
                {headCells.map((c) => (
                  <HeaderCell
                    key={c.id}
                    id={c.id}
                    sortable={c.sortable}
                    orderBy={orderBy}
                    order={order}
                    onSort={handleSort}
                    align={c.id === "_action" ? "right" : "left"}
                  >
                    {c.label}
                  </HeaderCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                Array.from({ length: rowsPerPage }).map((_, i) => (
                  <TableRow key={`sk-${i}`}>
                    {headCells.map((__, j) => (
                      <DataCell key={`sk-${i}-${j}`}>
                        <Skeleton variant="rectangular" height={18} sx={{ borderRadius: 1 }} />
                      </DataCell>
                    ))}
                  </TableRow>
                ))
              ) : isEmpty ? (
                <TableRow>
                  <TableCell colSpan={headCells.length} sx={{ borderBottom: "none" }}>
                    <EmptyState
                      icon={<GavelOutlinedIcon />}
                      title="No custom rules yet"
                      body="Use Add Custom Rule to create one."
                    />
                  </TableCell>
                </TableRow>
              ) : (
                paged.map((row, index) => {
                  const actions = [
                    {
                      key: "edit",
                      icon: <EditIcon fontSize="small" />,
                      label: "Edit",
                      onClick: () => editClick(row?.custom_rule_id),
                    },
                    { key: "div", divider: true },
                    {
                      key: "remove",
                      icon: <RemoveIcon fontSize="small" />,
                      label: "Remove permanently",
                      danger: true,
                      onClick: () => removeClick(row?.custom_rule_id, row?.isDeleted),
                    },
                  ];
                  const isLayerd = CrsSecRuleId.MIN_CUSTOM_GLOBAL <= row?.custom_rule_id;
                  return (
                    <TableRow
                      key={`${row?.custom_rule_id}-${index}`}
                      hover
                      sx={{ "&:hover": { background: TOKENS.bgSunken } }}
                    >
                      <DataCell>
                        <Typography
                          sx={{
                            fontSize: 13,
                            color: TOKENS.fg1,
                            fontWeight: 600,
                            fontFamily:
                              "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                          }}
                        >
                          {row?.custom_rule_id}
                        </Typography>
                      </DataCell>
                      <DataCell>
                        <Typography sx={{ fontSize: 13, color: TOKENS.fg1 }}>
                          {row?.description || "—"}
                        </Typography>
                      </DataCell>
                      <DataCell>
                        <Typography sx={{ fontSize: 13, color: TOKENS.fg1 }}>
                          {getRuleActionString(row?.action) || "—"}
                        </Typography>
                      </DataCell>
                      <DataCell>
                        <Typography sx={{ fontSize: 12.5, color: TOKENS.fg1 }}>
                          {row?.created_date ? formatDate(row.created_date) : "—"}
                        </Typography>
                      </DataCell>
                      <DataCell>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          {isLayerd ? (
                            <StatusLogo style={{ width: 18, height: 18 }} fill={TOKENS.successInk} />
                          ) : (
                            <UserIcon color={TOKENS.fg2} size={16} />
                          )}
                          <Typography sx={{ fontSize: 12.5, color: TOKENS.fg2 }}>
                            {isLayerd ? "Layerd AI Managed" : "User Managed"}
                          </Typography>
                        </Stack>
                      </DataCell>
                      <DataCell>
                        <IOSSwitch
                          disabled={disabled}
                          checked={!!row?.enabled}
                          onChange={() => customRuleChange(row?.custom_rule_id, !row?.enabled)}
                        />
                      </DataCell>
                      <DataCell align="right">
                        <RowMenu actions={actions} />
                      </DataCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {!isLoading && count > 0 ? (
          <Box
            sx={{
              p: "10px 16px",
              borderTop: `1px solid ${TOKENS.borderSubtle}`,
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              flexWrap: "wrap",
            }}
          >
            <Typography sx={{ fontSize: 12.5, color: TOKENS.fg2 }}>
              Page {page + 1} of {Math.max(1, last + 1)}
            </Typography>
            <Box sx={{ flex: 1 }} />
            <Typography
              component="button"
              type="button"
              onClick={() => page > 0 && setPage(page - 1)}
              disabled={page === 0}
              sx={{
                border: `1px solid ${TOKENS.border}`,
                borderRadius: `${TOKENS.radiusXs}px`,
                background: TOKENS.bgSurface,
                color: page === 0 ? TOKENS.fg3 : TOKENS.fg1,
                fontSize: 12.5,
                fontWeight: 600,
                px: 1.5,
                py: 0.5,
                cursor: page === 0 ? "not-allowed" : "pointer",
              }}
            >
              Prev
            </Typography>
            <Typography
              component="button"
              type="button"
              onClick={() => page < last && setPage(page + 1)}
              disabled={page >= last}
              sx={{
                border: `1px solid ${TOKENS.border}`,
                borderRadius: `${TOKENS.radiusXs}px`,
                background: TOKENS.bgSurface,
                color: page >= last ? TOKENS.fg3 : TOKENS.fg1,
                fontSize: 12.5,
                fontWeight: 600,
                px: 1.5,
                py: 0.5,
                cursor: page >= last ? "not-allowed" : "pointer",
              }}
            >
              Next
            </Typography>
            <Box
              component="select"
              value={rowsPerPage}
              onChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              sx={{
                border: `1px solid ${TOKENS.border}`,
                borderRadius: `${TOKENS.radiusXs}px`,
                background: TOKENS.bgSurface,
                color: TOKENS.fg1,
                fontSize: 12.5,
                px: 1,
                py: 0.5,
              }}
            >
              <option value={5}>5 / page</option>
              <option value={10}>10 / page</option>
              <option value={25}>25 / page</option>
            </Box>
          </Box>
        ) : null}
      </Box>

      <DeleteCustomRuleModal
        open={openDelete}
        handleClose={() => setOpenDelete(false)}
        ruleID={ruleID}
        deleted={deleted}
        removeFlag={removeFlag}
      />
    </Box>
  );
}

export default CustomRuleTable;
